import { GetObjectCommand, PutObjectCommand, type S3Client } from '@aws-sdk/client-s3'
import * as Sentry from '@sentry/react'
import {
  getJsonObject,
  isNotFoundError,
  isPreconditionFailedError,
  isRetryableError,
  paginateList,
  UPLOAD_BUCKET,
  withS3Client,
  type S3Io,
} from './s3'
import {
  compareSemver,
  getVersionsIndex,
  latestVersionOf,
  listVersionFolders,
  putVersionsIndex,
} from './toyVersions'
import { getCurrentIdentityId } from '../auth/currentIdentity'

export const TOY_INDEX_FILENAME = 'toy-index.json'
const METADATA_FILENAME = 'metadata.json'

export type ToyIndexEntry = Record<string, unknown>
export type ToyIndex = ToyIndexEntry[]

export function toyIndexKey(identityId: string): string {
  return `${identityId}/${TOY_INDEX_FILENAME}`
}

// The index plus the ETag it was read at, so a follow-up write can be made
// conditional (If-Match) and detect a concurrent modification. etag is null when
// the object doesn't exist yet (write with If-None-Match: '*' instead).
export type LoadedToyIndex = { index: ToyIndex; etag: string | null }

export async function loadToyIndexWithMeta(
  io: S3Io,
  identityId?: string,
): Promise<LoadedToyIndex> {
  const id = identityId ?? getCurrentIdentityId()
  return withS3Client(io, async (client) => {
    try {
      const res = await client.send(
        new GetObjectCommand({ Bucket: UPLOAD_BUCKET, Key: toyIndexKey(id) }),
      )
      const etag = res.ETag ?? null
      if (!res.Body) return { index: [], etag }
      const text = await res.Body.transformToString()
      if (!text.trim()) return { index: [], etag }
      const parsed = JSON.parse(text) as unknown
      if (!Array.isArray(parsed)) {
        throw new Error(`toy-index.json must be an array, got ${typeof parsed}`)
      }
      return { index: parsed as ToyIndex, etag }
    } catch (err) {
      if (isNotFoundError(err)) return { index: [], etag: null }
      throw err
    }
  })
}

export async function loadToyIndex(io: S3Io, identityId?: string): Promise<ToyIndex> {
  return (await loadToyIndexWithMeta(io, identityId)).index
}

export async function saveToyIndex(
  io: S3Io,
  index: ToyIndex,
  identityId?: string,
): Promise<void> {
  const id = identityId ?? getCurrentIdentityId()
  await withS3Client(io, async (client) => {
    const body = new TextEncoder().encode(JSON.stringify(index, null, 2))
    await client.send(
      new PutObjectCommand({
        Bucket: UPLOAD_BUCKET,
        Key: toyIndexKey(id),
        Body: body,
        ContentType: 'application/json',
      }),
    )
  })
}

export function appendEntry(index: ToyIndex, entry: ToyIndexEntry): ToyIndex {
  const id = typeof entry.id === 'string' ? entry.id : null
  const filtered = id ? index.filter((e) => e.id !== id) : index
  return [...filtered, entry]
}

// Move the entry at `from` to position `to`, returning a new array. Entry
// objects are carried by reference so unknown fields survive untouched. The
// input is returned as-is for no-op or out-of-range moves.
export function moveEntry(index: ToyIndex, from: number, to: number): ToyIndex {
  if (from === to) return index
  if (from < 0 || from >= index.length || to < 0 || to >= index.length) return index
  const next = [...index]
  const [entry] = next.splice(from, 1)
  next.splice(to, 0, entry)
  return next
}

const MAX_INDEX_COMMIT_ATTEMPTS = 5

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

function backoffMs(attempt: number): number {
  return Math.min(1000, 50 * 2 ** attempt) + Math.floor(Math.random() * 50)
}

// Conditional write of the index. precondition pins it to a specific prior state
// so a concurrent writer can't be silently clobbered: ifMatch for an existing
// object (its ETag), ifNoneMatch '*' to create only if absent.
async function putToyIndexConditional(
  io: S3Io,
  identityId: string,
  index: ToyIndex,
  precondition: { ifMatch: string } | { ifNoneMatch: string },
): Promise<void> {
  await withS3Client(io, async (client) => {
    const body = new TextEncoder().encode(JSON.stringify(index, null, 2))
    await client.send(
      new PutObjectCommand({
        Bucket: UPLOAD_BUCKET,
        Key: toyIndexKey(identityId),
        Body: body,
        ContentType: 'application/json',
        IfMatch: 'ifMatch' in precondition ? precondition.ifMatch : undefined,
        IfNoneMatch: 'ifNoneMatch' in precondition ? precondition.ifNoneMatch : undefined,
      }),
    )
  })
}

// Persist a manually reordered index. The entry order in toy-index.json is the
// display order of the public Featured page, so this must never merge or
// rebuild: it writes exactly the array given, conditional on the ETag the
// caller read it at. On a 412 (someone else changed the index) it throws —
// unlike appendToyToIndex there is no regenerate fallback, because the caller's
// order was derived from a stale index and must be redone, not guessed at.
export async function saveToyIndexOrder(
  io: S3Io,
  identityId: string,
  index: ToyIndex,
  etag: string | null,
): Promise<void> {
  await putToyIndexConditional(
    io,
    identityId,
    index,
    etag ? { ifMatch: etag } : { ifNoneMatch: '*' },
  )
}

// Commit a freshly-uploaded toy into toy-index.json. The index is only a cache
// of the per-toy folders on disk, so this guards two distinct hazards:
//   - drift: if the on-disk folders don't match the index (a prior lost write,
//     a direct upload), the cache is stale — rebuild it from disk rather than
//     trusting a blind append onto stale data.
//   - concurrency: appending is a read-modify-write, so two creations racing
//     could lose one. We write conditionally on the ETag we read and retry on a
//     412, so a concurrent writer's entry is never clobbered. After exhausting
//     retries we fall back to the disk-authoritative rebuild (a single write).
export async function appendToyToIndex(
  io: S3Io,
  identityId: string,
  entry: ToyIndexEntry,
): Promise<void> {
  const newId = typeof entry.id === 'string' ? entry.id : null
  for (let attempt = 0; attempt < MAX_INDEX_COMMIT_ATTEMPTS; attempt++) {
    const [{ index, etag }, diskToyIds] = await Promise.all([
      loadToyIndexWithMeta(io, identityId),
      listUserToys(io, identityId),
    ])
    const expectedIds = new Set<string>(newId ? [newId] : [])
    for (const e of index) {
      if (typeof e.id === 'string') expectedIds.add(e.id)
    }
    const diskIds = new Set(diskToyIds)
    const inSync =
      expectedIds.size === diskIds.size && [...diskIds].every((id) => expectedIds.has(id))
    if (!inSync) {
      await regenerateToyIndex(io, identityId)
      return
    }
    try {
      await putToyIndexConditional(
        io,
        identityId,
        appendEntry(index, entry),
        etag ? { ifMatch: etag } : { ifNoneMatch: '*' },
      )
      return
    } catch (err) {
      if (!isPreconditionFailedError(err) && !isRetryableError(err)) throw err
      // Concurrent write (412) or a transient error: reload and retry, unless
      // we're out of attempts — then fall through to the rebuild below.
      if (attempt < MAX_INDEX_COMMIT_ATTEMPTS - 1) await sleep(backoffMs(attempt))
    }
  }
  // Persistent contention or repeated transient failures: rebuild from disk,
  // which is authoritative and needs no read-modify-write.
  await regenerateToyIndex(io, identityId)
}

// Lists the first-level toy folders (their ids) under an identity. The toy
// index is just a cache of these folders, so this is the source of truth used
// to detect drift and to rebuild the index from disk.
export async function listUserToys(io: S3Io, identityId: string): Promise<string[]> {
  return withS3Client(io, async (client) => {
    const toys: string[] = []
    const prefix = `${identityId}/`
    for await (const res of paginateList(client, {
      Bucket: UPLOAD_BUCKET,
      Prefix: prefix,
      Delimiter: '/',
    })) {
      for (const p of res.CommonPrefixes ?? []) {
        if (!p.Prefix) continue
        const name = p.Prefix.slice(prefix.length).replace(/\/$/, '')
        if (name) toys.push(name)
      }
    }
    return toys
  })
}

// Fetches and validates one metadata.json. Missing objects resolve to null
// (an expected mid-upload state); corrupt (parseable-but-wrong / malformed)
// metadata also resolves to null but is reported to Sentry since it silently
// loses an app.
async function getMetadataAt(
  client: S3Client,
  identityId: string,
  toyId: string,
  key: string,
): Promise<ToyIndexEntry | null> {
  const result = await getJsonObject(client, UPLOAD_BUCKET, key)
  if (result.kind === 'ok') return result.value as ToyIndexEntry
  if (result.kind === 'invalid-json') {
    // Corrupt JSON drops the toy from the index — capture it so the data
    // corruption is visible, not just logged to a console nobody reads.
    console.warn('[toy-index] skipping malformed metadata.json', { toyId, err: result.error })
    Sentry.captureException(result.error, { extra: { identityId, toyId } })
  }
  if (result.kind === 'not-object') {
    // Parseable but wrong shape: the toy is silently dropped from the
    // rebuilt index, so surface it to Sentry rather than only the console.
    console.warn('[toy-index] skipping metadata.json with non-object shape', { toyId })
    Sentry.captureMessage('regenerateToyIndex: metadata.json has non-object shape', {
      level: 'warning',
      extra: { identityId, toyId },
    })
  }
  return null
}

// Finds the metadata.json describing a toy's latest version, spanning both
// layout eras: versions-index.json → version folders actually on disk → the
// legacy un-versioned toy root. Every version folder is tried, newest first —
// the versions-index may be missing or corrupt, and any folder (including the
// highest, e.g. a half-uploaded staging) may lack readable metadata.
async function loadLatestMetadataForToy(
  client: S3Client,
  identityId: string,
  toyId: string,
): Promise<ToyIndexEntry | null> {
  const versionsIndex = await getVersionsIndex(client, identityId, toyId)
  const latest = versionsIndex ? latestVersionOf(versionsIndex) : null
  if (latest) {
    const entry = await getMetadataAt(
      client,
      identityId,
      toyId,
      `${identityId}/${toyId}/${latest}/${METADATA_FILENAME}`,
    )
    if (entry) return entry
  }
  const folders = (await listVersionFolders(client, identityId, toyId))
    .filter((v) => v !== latest)
    .sort((a, b) => compareSemver(b, a))
  for (const version of folders) {
    const entry = await getMetadataAt(
      client,
      identityId,
      toyId,
      `${identityId}/${toyId}/${version}/${METADATA_FILENAME}`,
    )
    if (!entry) continue
    if (!versionsIndex) {
      // Self-heal a missing versions-index from the folders on disk, with
      // latest pointing at the version that actually has readable metadata.
      // Best effort: a failed write must not take the index rebuild down.
      try {
        await putVersionsIndex(client, identityId, toyId, {
          schemaVersion: 1,
          latest: version,
          versions: folders.map((v) => ({ version: v })),
        })
      } catch (err) {
        console.warn('[toy-index] failed to self-heal versions-index.json', { toyId, err })
        Sentry.captureException(err, { extra: { identityId, toyId } })
      }
    }
    return entry
  }
  return getMetadataAt(client, identityId, toyId, `${identityId}/${toyId}/${METADATA_FILENAME}`)
}

// Rebuilds toy-index.json from the metadata.json files actually on disk,
// overwriting whatever was there. Each entry is the latest version's metadata.
// Any toy whose metadata.json is missing or unreadable is dropped.
export async function regenerateToyIndex(
  io: S3Io,
  identityId: string,
): Promise<{ entryCount: number }> {
  const [toyIds, previousIndex] = await Promise.all([
    listUserToys(io, identityId),
    loadToyIndex(io, identityId),
  ])
  const entries = await withS3Client(io, async (client) => {
    const results = await Promise.all(
      toyIds.map((toyId) => loadLatestMetadataForToy(client, identityId, toyId)),
    )
    return results.filter((e): e is ToyIndexEntry => e !== null)
  })

  // The array order is the public Featured page's display order and may have
  // been arranged by hand, so a rebuild keeps each surviving toy's position.
  // New toys go at the end in id order — deterministic regardless of
  // Promise.all resolution order.
  const previousPosition = new Map<string, number>()
  previousIndex.forEach((e, i) => {
    if (typeof e.id === 'string') previousPosition.set(e.id, i)
  })
  entries.sort((a, b) => {
    const ai = typeof a.id === 'string' ? a.id : ''
    const bi = typeof b.id === 'string' ? b.id : ''
    const ap = previousPosition.get(ai) ?? Infinity
    const bp = previousPosition.get(bi) ?? Infinity
    if (ap !== bp) return ap - bp
    return ai.localeCompare(bi)
  })
  await saveToyIndex(io, entries, identityId)
  return { entryCount: entries.length }
}
